import React from 'react';
import { useTranslation } from 'react-i18next';
import { Droplets, Battery, BatteryLow, WifiOff } from 'lucide-react';

const BATTERY_STATE_LABEL = { low: 'low', middle: 'middle', high: 'high' };

function timeAgo(iso, lang) {
    if (!iso) return null;
    const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    if (secs < 3600) return rtf.format(-Math.max(1, Math.round(secs / 60)), 'minute');
    if (secs < 86400) return rtf.format(-Math.round(secs / 3600), 'hour');
    return rtf.format(-Math.round(secs / 86400), 'day');
}

export function SensorCard({ sensor, color, selected, onToggle }) {
    const { t, i18n } = useTranslation();
    const hasValue = sensor.temperature !== null && sensor.temperature !== undefined;
    const lowBattery = (sensor.battery_pct !== null && sensor.battery_pct <= 20) || sensor.battery_state === 'low';
    const BatteryIcon = lowBattery ? BatteryLow : Battery;
    const ago = timeAgo(sensor.last_reported_at, i18n.language);

    return (
        <button type="button" onClick={onToggle} aria-pressed={selected}
            className={`text-left bg-card border rounded-xl p-4 transition-all ${selected ? 'border-primary/60 shadow-sm' : 'border-border opacity-60'}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
                <span className="flex items-center gap-2 font-medium capitalize">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
                    {sensor.name}
                </span>
                {!sensor.online && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <WifiOff className="w-3.5 h-3.5" />{t('sensors.offline')}
                    </span>
                )}
            </div>

            <div className="flex items-end justify-between">
                <span className={`text-3xl font-bold tabular-nums ${sensor.online ? '' : 'text-muted-foreground'}`}>
                    {hasValue ? `${sensor.temperature.toFixed(1)}°` : '–'}
                </span>
                {sensor.humidity !== null && sensor.humidity !== undefined && (
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Droplets className="w-4 h-4" />{Math.round(sensor.humidity)} %
                    </span>
                )}
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                    {ago ? t('sensors.updated', { when: ago }) : t('sensors.noData')}
                </span>
                {(sensor.battery_pct !== null || sensor.battery_state) && (
                    <span className={`flex items-center gap-1 ${lowBattery ? 'text-red-500' : ''}`}>
                        <BatteryIcon className="w-3.5 h-3.5" />
                        {sensor.battery_pct !== null ? `${sensor.battery_pct} %` : t(`sensors.battery.${BATTERY_STATE_LABEL[sensor.battery_state]}`)}
                    </span>
                )}
            </div>

            {sensor.min_24h !== null && sensor.max_24h !== null && (
                <div className="mt-1 text-xs text-muted-foreground">
                    {t('sensors.range24h', { min: sensor.min_24h.toFixed(1), max: sensor.max_24h.toFixed(1) })}
                </div>
            )}
        </button>
    );
}
